import {
  currentDate,
  customFormatDate,
  differenceInDays,
  differenceInMonths,
  formatTextDate,
  getLastMonth,
  getLastWeek,
  getLastYear,
  getMonthToDate,
  getPastNinetyDays,
  getPastSevenDays,
  getPastThirtyDays,
  getThisWeek,
  getToday,
  getYearToDate,
  getYesterday,
} from '@witmetrics/utils/time';
import type { DateRange, Timeframe, TimePeriod } from '@witmetrics/utils';
import {
  LAST_MONTH,
  LAST_WEEK,
  LAST_YEAR,
  MMDD,
  MONTH_TO_DATE,
  PAST_30_DAYS,
  PAST_7_DAYS,
  PAST_90_DAYS,
  THIS_WEEK,
  TIME_PERIOD,
  TODAY,
  YEAR_TO_DATE,
  YESTERDAY,
} from '@witmetrics/utils/constants';

export function formatTimeLabel(
  date: string,
  timePeriod: TimePeriod,
  dateFormat = MMDD
) {
  if (timePeriod === TIME_PERIOD.YEAR) {
    return renderYear(date);
  } else if (timePeriod === TIME_PERIOD.MONTH) {
    return renderMonthWithYear(date);
  }
  return formatTextDate(date, dateFormat);
}

export function renderMonth(ISODateString: string) {
  return customFormatDate(ISODateString, 'MMM');
}

export function renderMonthWithYear(
  ISODateString: string,
  yearFormat = 'yyyy'
) {
  return customFormatDate(ISODateString, `MMM ${yearFormat}`);
}

export function renderYear(ISODateString: string) {
  return customFormatDate(ISODateString, 'yyyy');
}

export function renderTimeframe(
  start: string,
  end: string,
  dateFormat?: string
) {
  const startLabel = start ? formatTextDate(start, dateFormat) : '--';
  const endLabel = end ? formatTextDate(end, dateFormat) : '--';
  return `${startLabel} - ${endLabel}`;
}

export function buildExportTimestamp(startDate?: string, endDate?: string) {
  if (!startDate) return customFormatDate(currentDate(), 'yyyyMMdd-HHmmss');
  const start = customFormatDate(startDate, 'yyyyMMdd');
  if (!endDate) return start;
  return `${start}-${customFormatDate(endDate, 'yyyyMMdd')}`;
}

// Timeframe methods

export function getDefinedTimeframe(
  timeframe: Timeframe,
  weekStartsOn: Day = 0
): DateRange {
  switch (timeframe) {
    case TODAY:
      return getToday();
    case YESTERDAY:
      return getYesterday();
    case THIS_WEEK:
      return getThisWeek(weekStartsOn);
    case PAST_7_DAYS:
      return getPastSevenDays();
    case LAST_WEEK:
      return getLastWeek(weekStartsOn);
    case MONTH_TO_DATE:
      return getMonthToDate();
    case LAST_MONTH:
      return getLastMonth();
    case PAST_30_DAYS:
      return getPastThirtyDays();
    case PAST_90_DAYS:
      return getPastNinetyDays();
    case YEAR_TO_DATE:
      return getYearToDate();
    case LAST_YEAR:
      return getLastYear();
    default:
      throw Error(`Unrecognized timeframe: "${timeframe}"`);
  }
}

export function getDefaultTimePeriod(start: string, end: string) {
  const days = Math.abs(differenceInDays(end, start));
  const months = Math.abs(differenceInMonths(end, start));
  if (days <= 31) {
    return TIME_PERIOD.DAY;
  } else if (months <= 4) {
    return TIME_PERIOD.WEEK;
  } else if (months <= 24) {
    return TIME_PERIOD.MONTH;
  } else return TIME_PERIOD.YEAR;
}

// Day-level trends get slow/cramped over long ranges: warn past 90 days,
// block past 180 (the API copes fine; it's a load-time + readability limit).
export const DAY_LEVEL_WARN_DAYS = 90;
export const DAY_LEVEL_MAX_DAYS = 180;

export function getTimeframeDays([start, end]: DateRange) {
  return Math.abs(differenceInDays(end, start));
}

export function isTimeframeChanged(
  timeframe1: DateRange,
  timeframe2: DateRange
) {
  return timeframe1[0] !== timeframe2[0] || timeframe1[1] !== timeframe2[1];
}

export function splitSeconds(timeInSeconds: number) {
  const minutes = Math.floor(timeInSeconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  return {
    days,
    hours: hours % 24,
    minutes: minutes % 60,
    seconds: timeInSeconds % 60,
  };
}
